import * as Sentry from '@sentry/react';

const anonymousUsername = '(Anonymous)';

/** Thin logging wrapper that mirrors shell events into Sentry and the console. */
class Logger {
    constructor() {
        this.setAnonymousUser();
    }

    /**
     * Associates future Sentry events with the provided username.
     *
     * @param username Authenticated username or display name.
     */
    public setUser(username: string): void {
        Sentry.setUser({username});
    }

    /** Clears the active Sentry user back to the anonymous placeholder. */
    public setAnonymousUser(): void {
        Sentry.setUser({username: anonymousUsername});
    }

    /**
     * Logs informational messages to the console and Sentry.
     *
     * @param info Message to record.
     */
    public logInfo(info: string): void {
        // eslint-disable-next-line no-console
        console.info(info);
        Sentry.captureEvent({
            message: info,
            level: 'info',
        });
    }

    /**
     * Logs warnings to the console and Sentry.
     *
     * @param warn Warning text to record.
     */
    public logWarn(warn: string): void {
        // eslint-disable-next-line no-console
        console.warn(warn);
        Sentry.captureEvent({
            message: warn,
            level: 'warning',
        });
    }

    /**
     * Logs errors to the console and Sentry.
     *
     * @param err Error object to capture.
     */
    public logError(err: Error): void {
        // eslint-disable-next-line no-console
        console.error(err);
        Sentry.captureException(err);
    }
}

/**
 * Shared application logger used by shell modules and feature flows.
 *
 * @example
 * logger.logInfo('Application shell started.');
 */
export const logger = new Logger();
